/** Single source of truth for Super Admin sidebar navigation and role page permissions. */

export interface SidebarNavPage {
  /** Stable permission id — derived from route path when omitted. */
  id: string;
  label: string;
  path: string;
  icon: string;
  /** Extra paths (exact or prefix with trailing *) that activate this page / open its group. */
  matchPaths?: string[];
}

export interface SidebarNavGroup {
  id: string;
  label: string;
  icon: string;
  pages: SidebarNavPage[];
}

function page(
  label: string,
  path: string,
  icon: string,
  matchPaths?: string[],
  id?: string,
): SidebarNavPage {
  return {
    id: id || path.replace(/^\//, "").replace(/\//g, "-"),
    label,
    path,
    icon,
    matchPaths,
  };
}

export const SUPER_ADMIN_SIDEBAR_DASHBOARD: SidebarNavPage = page(
  "Dashboard",
  "/dashboard",
  "bi-speedometer2",
);

export const SUPER_ADMIN_SIDEBAR_GROUPS: SidebarNavGroup[] = [
  {
    id: "music",
    label: "Music",
    icon: "bi-music-note-list",
    pages: [
      page("Album", "/albums", "bi-disc"),
      page("Artists", "/artists", "bi-mic"),
      page("Channel Admin", "/channel-admin", "bi-gear"),
      page("Channel Dashboard", "/music-channel-programming", "bi-broadcast"),
      page(
        "Channel Programming",
        "/channel-programming-overview",
        "bi-calendar-event",
        ["/channel-programming-overview", "/channel-programming/*"],
      ),
      page("Genres", "/genres", "bi-tags"),
      page("Live Plays Tracker", "/live-plays-tracker", "bi-graph-up"),
      page("Record Labels", "/record-labels", "bi-building"),
      page("Song Library", "/songs", "bi-collection"),
      page("Song Uploader", "/uploader", "bi-cloud-arrow-up"),
      page(
        // Operator-facing name (owner 2026-07-21); route/API names unchanged.
        "Song Processing",
        "/processing-jobs",
        "bi-cpu",
        ["/processing-jobs", "/processing-jobs/*"],
        "uploader",
      ),
      page("Song History", "/uploader-history", "bi-clock-history", [
        "/uploader-history",
        "/uploader-history/*",
      ]),
      page(
        "Song Intake",
        "/music-upload-reviews",
        "bi-clipboard-check",
        ["/music-upload-reviews", "/music-upload-reviews/*"],
        "uploader",
      ),
    ],
  },
  {
    id: "messaging",
    label: "Messaging",
    icon: "bi-chat-dots",
    pages: [
      page("Talents", "/talents", "bi-star"),
      page("Talent Requests", "/talent-requests", "bi-person-plus"),
      page("Message Requests", "/message-requests", "bi-chat-square-text"),
    ],
  },
  {
    id: "accounts",
    label: "Accounts",
    icon: "bi-people",
    pages: [
      page("Brands", "/brands", "bi-tag", ["/brands", "/brands/*"]),
      page("Clients", "/accounts", "bi-people", ["/accounts", "/accounts/*"], "clients"),
      page("Client Support", "/client-support", "bi-headset", ["/client-support"], "client-support"),
      page("Client Roles", "/client-roles", "bi-person-badge"),
      page("LLT", "/llt", "bi-link-45deg"),
      page("Devices", "/devices", "bi-hdd"),
      page("Device Assignments", "/inventory-device-assignments", "bi-laptop"),
      page("Transactions", "/transactions", "bi-credit-card"),
    ],
  },
  {
    id: "inventory",
    label: "Inventory",
    icon: "bi-box-seam",
    pages: [
      page("Activity", "/inventory-activity", "bi-graph-up"),
      page("Categories", "/categories", "bi-tags", ["/categories", "/inventory-categories/*"]),
      page("Products", "/inventory-products", "bi-box", ["/inventory-products", "/inventory-products/*"]),
      page("Product Inventory", "/product-inventory", "bi-boxes"),
      page("Shipping", "/inventory-shipping", "bi-truck"),
      page("Warehouse", "/wearhouse", "bi-building"),
    ],
  },
  {
    id: "vendors",
    label: "Vendors",
    icon: "bi-shop",
    pages: [
      page("Music Vendors", "/music-vendors", "bi-music-note-beamed", ["/music-vendors", "/music-vendors/*"]),
      page("Music Vendor Roles", "/music-vendor-roles", "bi-person-gear"),
      page("Installer Vendors", "/installer-vendors", "bi-tools", ["/installer-vendors", "/installer-vendors/*"]),
      page("Installer Vendor Roles", "/installer-vendor-roles", "bi-person-gear"),
      page("Pending Approvals (Vendors)", "/pending-approvals", "bi-hourglass-split"),
    ],
  },
  {
    id: "licensing",
    label: "Licensing",
    icon: "bi-clipboard-check",
    pages: [
      page("Trackers", "/trackers", "bi-graph-up-arrow"),
      page("PROs Location Reports", "/pros-location-reports", "bi-building-check"),
    ],
  },
  {
    id: "development",
    label: "Development",
    icon: "bi-tools",
    pages: [page("Releases", "/releases", "bi-rocket")],
  },
  {
    id: "pricing",
    label: "Pricing",
    icon: "bi-currency-dollar",
    pages: [
      page("Pricing Music Plans", "/pricing-music-plans", "bi-music-note-list"),
      page("Pricing Devices", "/pricing-devices", "bi-hdd"),
    ],
  },
  {
    id: "my-account",
    label: "My Account",
    icon: "bi-person-badge",
    pages: [
      page("My Profile", "/account/profile", "bi-person", undefined, "account-profile"),
      page("Security", "/account/security", "bi-shield-lock", undefined, "account-security"),
      page("Company Profile", "/account/company-profile", "bi-building", undefined, "account-company-profile"),
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: "bi-shield-lock",
    pages: [
      page("Employees", "/employees", "bi-people"),
      page("Employee Roles", "/employee-roles", "bi-person-badge"),
      page("Employee Departments", "/employee-departments", "bi-diagram-3"),
      page("Activity History", "/activity-history", "bi-clock-history"),
    ],
  },
];

function pathsForPage(navPage: SidebarNavPage): string[] {
  return [navPage.path, ...(navPage.matchPaths || [])];
}

function pathMatches(pathname: string, pattern: string): boolean {
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -1);
    return pathname === prefix.slice(0, -1) || pathname.startsWith(prefix);
  }
  return pathname === pattern;
}

export function isSidebarPageActive(pathname: string, navPage: SidebarNavPage): boolean {
  return pathsForPage(navPage).some((pattern) => pathMatches(pathname, pattern));
}

export function isSidebarGroupActive(pathname: string, group: SidebarNavGroup): boolean {
  return group.pages.some((navPage) => isSidebarPageActive(pathname, navPage));
}

export function getSidebarPermissionSections(): SidebarNavGroup[] {
  return [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: SUPER_ADMIN_SIDEBAR_DASHBOARD.icon,
      pages: [SUPER_ADMIN_SIDEBAR_DASHBOARD],
    },
    ...SUPER_ADMIN_SIDEBAR_GROUPS,
  ];
}

export function getAllSidebarPermissionPageIds(): string[] {
  return getSidebarPermissionSections().flatMap((group) =>
    group.pages.map((navPage) => navPage.id),
  );
}

export function findSidebarGroup(groupId: string): SidebarNavGroup | undefined {
  return getSidebarPermissionSections().find((group) => group.id === groupId);
}

/** Nested routes not listed on the sidebar but owned by a permission page. */
const SUPPLEMENTAL_ROUTE_OWNERS: { pattern: string; pageId: string }[] = [
  { pattern: "/client-locations/*", pageId: "clients" },
  { pattern: "/roles", pageId: "client-roles" },
];

export function resolvePageIdFromPathname(pathname: string): string | null {
  const path = String(pathname || "").split("?")[0].replace(/\/+$/, "") || "/";

  for (const { pattern, pageId } of SUPPLEMENTAL_ROUTE_OWNERS) {
    if (pathMatches(path, pattern)) return pageId;
  }

  for (const group of getSidebarPermissionSections()) {
    for (const navPage of group.pages) {
      if (pathsForPage(navPage).some((pattern) => pathMatches(path, pattern))) {
        return navPage.id;
      }
    }
  }

  return null;
}
